import { useCallback, useEffect, useRef, useState } from 'react'

import { useAppSelector } from './hooks'
import { selectStatus } from './sessionSelectors'
import { silentLogin } from './silentLogin'

/** Не чаще одной перепроверки «кто я» за этот срок: каждое переключение вкладки — не запрос. */
export const RECHECK_THROTTLE_MS = 10_000
/** Не чаще одной попытки тихого входа за этот срок: защита от петли, если SSO отвечает отказом. */
export const SILENT_LOGIN_COOLDOWN_MS = 60_000

/**
 * Самовосстановление сессии без участия человека. Сервер продлевает cookie-сессию
 * сам, а клиент раньше запоминал первый же 401 навсегда: баннер «Сессия завершена»
 * снимало только обновление страницы. Теперь:
 *
 * 1. возврат на вкладку / появление сети / фокус окна перечитывают `/auth/me` (с
 *    ограничением частоты): сессию могли продлить или оборвать, пока вкладка спала;
 * 2. статус «истекла» (401 на ЛЮБОМ запросе) сначала перепроверяется тем же `/auth/me`
 *    — 401 чужого запроса сессию не рвёт; ответил — статус снова «активна» (его
 *    возвращает listener `sessionMe.matchFulfilled`);
 * 3. `/auth/me` тоже отказал — один тихий вход по SSO через скрытый iframe, не чаще
 *    раза в минуту; не вышло — остаётся кнопка «Войти». Уход на вход без клика не делаем.
 *
 * `recovering` — идёт перепроверка: баннер на это время прячется, чтобы не мигать.
 */
export function useSessionRecovery(refetch: () => Promise<{ isSuccess: boolean; isError: boolean; error?: unknown }>): {
  recovering: boolean
} {
  const status = useAppSelector(selectStatus)
  const [running, setRunning] = useState(false)
  /** Перепроверка для текущего «истекла» уже была: дальше решает человек (кнопка «Войти»). */
  const [handled, setHandled] = useState(false)
  const lastCheckAt = useRef(0)
  const lastSilentAt = useRef(-Infinity)
  const inFlight = useRef(false)
  const refetchRef = useRef(refetch)
  useEffect(() => {
    refetchRef.current = refetch
  })

  const recover = useCallback(async () => {
    if (inFlight.current) return
    inFlight.current = true
    setRunning(true)
    try {
      lastCheckAt.current = Date.now()
      const first = await refetchRef.current()
      if (first.isSuccess || !isUnauthorized(first.error)) return
      if (Date.now() - lastSilentAt.current < SILENT_LOGIN_COOLDOWN_MS) return
      lastSilentAt.current = Date.now()
      if (await silentLogin()) await refetchRef.current()
    } finally {
      inFlight.current = false
      setHandled(true)
      setRunning(false)
    }
  }, [])

  // 401 на любом запросе → статус «истекла» → перепроверка.
  useEffect(() => {
    if (status === 'expired') void recover()
  }, [status, recover])
  // Эпизод «истекла» кончился — отметка о перепроверке сбрасывается тут же, без лишнего кадра.
  if (status !== 'expired' && handled) setHandled(false)

  // Вкладка проснулась.
  useEffect(() => {
    const wake = () => {
      if (document.visibilityState === 'hidden') return
      if (status !== 'active' && status !== 'expired') return
      if (Date.now() - lastCheckAt.current < RECHECK_THROTTLE_MS) return
      void recover()
    }
    document.addEventListener('visibilitychange', wake)
    window.addEventListener('focus', wake)
    window.addEventListener('online', wake)
    return () => {
      document.removeEventListener('visibilitychange', wake)
      window.removeEventListener('focus', wake)
      window.removeEventListener('online', wake)
    }
  }, [status, recover])

  // Выводится из статуса, а не из эффекта: иначе первый кадр успевал показать кнопку «Войти».
  return { recovering: running || (status === 'expired' && !handled) }
}

function isUnauthorized(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { kind?: unknown }).kind === 'unauthorized'
}
